import { useCallback } from 'react';
import { authApi } from '@/api/endpoints/auth';
import { isApiError } from '@/api/errors';
import type { DetailViewsQuota } from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { usePaywall } from '@/billing/PaywallContext';
import { useCanvases } from '@/library/useCanvases';
import { quotaStore } from './quotaStore';
import { useAccess } from './useAccess';

/** Служебная доска закладок в live — не канвас пользователя. */
const SAVED_BOARD_NAME = 'Saved';

/**
 * Пределы бесплатного тарифа (бэкенд: config/free_tier.php). Кнопки не
 * блокируются: гейт по клику открывает окно оплаты с причиной и возвращает
 * false. Правило доступа одно — useAccess(); здесь только применение.
 */
export function useFreeTier() {
  const access = useAccess();
  const { user } = useAuth();
  const { data: canvases } = useCanvases();
  const { openPaywall } = usePaywall();

  const isFree =
    access.isAccessResolved && access.isPaywallEnabled && !access.hasFullAccess && !!user;
  const canvasLimit = user?.quota?.canvases.limit ?? 1;
  const canvasesUsed = (canvases ?? []).filter((c) => c.name !== SAVED_BOARD_NAME).length;

  const guardCreateCanvas = useCallback((): boolean => {
    if (!isFree || canvasesUsed < canvasLimit) return true;
    openPaywall({ trigger: 'tripwire', source: 'canvas_limit', reason: 'canvas_limit' });
    return false;
  }, [isFree, canvasesUsed, canvasLimit, openPaywall]);

  const guardShareCanvas = useCallback((): boolean => {
    if (!isFree) return true;
    openPaywall({ trigger: 'tripwire', source: 'canvas_share', reason: 'canvas_share' });
    return false;
  }, [isFree, openPaywall]);

  /** Списать детальный просмотр до открытия панели; при исчерпании — окно оплаты вместо панели. */
  const guardDetailView = useCallback(
    async (videoId: number): Promise<boolean> => {
      if (!isFree) return true;
      try {
        const result = await authApi.registerDetailView(videoId);
        quotaStore.set(result.quota);
        return true;
      } catch (error) {
        if (isApiError(error) && error.code === 'detail_quota_exceeded') {
          quotaStore.set((error.data as { quota?: DetailViewsQuota | null } | null)?.quota ?? null);
          openPaywall({ trigger: 'tripwire', source: 'detail_quota', reason: 'detail_quota' });
          return false;
        }
        // Сеть или 5xx — не запирать панель из-за счётчика.
        return true;
      }
    },
    [isFree, openPaywall],
  );

  return {
    isFree,
    canvasLimit,
    canvasesUsed,
    guardCreateCanvas,
    guardShareCanvas,
    guardDetailView,
  };
}
